import insertEv from "@cclib/functions/eventGroups/insertEvent.js";
import tcLib from "@cclib/lib/timecode.js";
import { createLiveCaptionAdapter, getPlatformConfig, PLATFORM_IDS } from "@app/lib/liveCaption/index.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";

const STORAGE_KEY = "cc-live-caption-settings";

let activeAdapter = null;

function loadLiveCaptionSettings() {
    const defaults = {
        platform: PLATFORM_IDS.YOUTUBE,
        endpoint: "",
        auth: "",
        rows: 3,
        maxChars: getPlatformConfig(PLATFORM_IDS.YOUTUBE).defaultMaxChars,
        customMaxChars: 80,
        autoWrap: false,
        captionChannel: "CC1",
        latencyOffset: 0,
        youtubeRegion: 8,
        targetEventGroup: "active",
        fontSize: 16,
        showCharCounter: true,
        showSessionTimer: true,
    };

    try {
        return {
            ...defaults,
            ...(JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}),
        };
    } catch (err) {
        console.error(err.message);
        return defaults;
    }
}

function createLine(text, type = "caption") {
    return {
        id: globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        text,
        type,
        sentAt: new Date().toISOString(),
    };
}

function getTargetEventGroupIndex() {
    if (liveCaptionState.targetEventGroup === "active") {
        return projectState.selected;
    }

    return Number(liveCaptionState.targetEventGroup);
}

function getWallClockTimecode(date = new Date()) {
    const frameRate = projectState.frameRate || 29.97;
    const frame = Math.floor((date.getMilliseconds() / 1000) * frameRate);
    const separator = frameRate === 29.97 && projectState.dropFrame ? ";" : ":";

    return [
        date.getHours().toString().padStart(2, "0"),
        date.getMinutes().toString().padStart(2, "0"),
        date.getSeconds().toString().padStart(2, "0"),
    ].join(":") + separator + frame.toString().padStart(2, "0");
}

function getSeqContext() {
    const seq = liveCaptionSession.seq;
    liveCaptionSession.seq += 1;
    return { seq };
}

function getSendContext() {
    return liveCaptionState.platform === PLATFORM_IDS.YOUTUBE ? getSeqContext() : {};
}

function createAdapter() {
    activeAdapter = createLiveCaptionAdapter({
        ...JSON.parse(JSON.stringify(liveCaptionState)),
    });

    return activeAdapter;
}

function ensureAdapter() {
    return activeAdapter || createAdapter();
}

function getElapsedSeconds() {
    if (!liveCaptionSession.sessionStartMs) {
        return 0;
    }

    return Math.max(0, (Date.now() - liveCaptionSession.sessionStartMs) / 1000);
}

function getLiveCaptionPosition() {
    const positions = {
        1: { xPos: "start", yPos: "start", alignment: "left" },
        2: { xPos: "center", yPos: "start", alignment: "center" },
        3: { xPos: "end", yPos: "start", alignment: "right" },
        4: { xPos: "start", yPos: "center", alignment: "left" },
        5: { xPos: "center", yPos: "center", alignment: "center" },
        6: { xPos: "end", yPos: "center", alignment: "right" },
        7: { xPos: "start", yPos: "end", alignment: "left" },
        8: { xPos: "center", yPos: "end", alignment: "center" },
        9: { xPos: "end", yPos: "end", alignment: "right" },
    };

    return positions[Number(liveCaptionState.youtubeRegion)] || positions[8];
}

function insertLiveCaptionEvent(text, startSec) {
    const eventGroupIndex = getTargetEventGroupIndex();

    if (eventGroupIndex === false || !eventGroupState[eventGroupIndex]) {
        throw new Error("Select an Event Group before sending live captions.");
    }

    if (liveCaptionSession.pendingEvent?.eventGroupIndex === eventGroupIndex) {
        const previousEvent = eventGroupState[eventGroupIndex].events[liveCaptionSession.pendingEvent.eventIndex];
        if (previousEvent && startSec > previousEvent.start) {
            previousEvent.end = startSec;
        }
    }

    eventGroupState[eventGroupIndex] = insertEv(eventGroupState[eventGroupIndex], {
        text,
        style: `Roll-Up ${liveCaptionState.rows}`,
        start: startSec,
        end: startSec + (eventGroupState[eventGroupIndex].minDuration || 1),
        ...getLiveCaptionPosition(),
    }, null);

    const eventIndex = eventGroupState[eventGroupIndex].events.length - 1;
    eventGroupState[eventGroupIndex].selected = [eventIndex];
    liveCaptionSession.pendingEvent = { eventGroupIndex, eventIndex };

    insertHistoryAction({
        name: "live caption",
        eventGroup: eventGroupIndex,
        snapshots: [{ store: "eventGroupState", value: JSON.stringify(eventGroupState) }],
    });
}

function addTranscriptLine(text, type = "caption") {
    liveCaptionSession.transcript.push(createLine(text, type));
    liveCaptionSession.rollupBuffer.push(text);

    while (liveCaptionSession.rollupBuffer.length > Number(liveCaptionState.rows)) {
        liveCaptionSession.rollupBuffer.shift();
    }
}

export const liveCaptionState = $state(loadLiveCaptionSettings());

export const liveCaptionSession = $state({
    enabled: false,
    connectionStatus: "idle",
    seq: 1,
    sessionStartMs: null,
    rollupBuffer: [],
    transcript: [],
    lastError: "",
    pendingEvent: null,
});

export function saveLiveCaptionSettings() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(liveCaptionState));
}

export function applyLiveCaptionPlatformDefaults(platform = liveCaptionState.platform) {
    const platformConfig = getPlatformConfig(platform);
    liveCaptionState.platform = platformConfig.id;
    liveCaptionState.maxChars = platformConfig.defaultMaxChars;
}

export async function testLiveCaptionConnection() {
    const adapter = createAdapter();
    liveCaptionSession.connectionStatus = "connecting";

    try {
        await adapter.test(getSendContext());
        liveCaptionSession.connectionStatus = liveCaptionSession.enabled ? "connected" : "idle";
        liveCaptionSession.lastError = "";
        return true;
    } catch (err) {
        liveCaptionSession.connectionStatus = "error";
        liveCaptionSession.lastError = err.message;
        throw err;
    } finally {
        if (!liveCaptionSession.enabled) {
            await adapter.disconnect?.();
            activeAdapter = null;
        }
    }
}

export async function enableLiveCaptionSession() {
    liveCaptionSession.connectionStatus = "connecting";
    liveCaptionSession.lastError = "";

    const adapter = createAdapter();
    await adapter.connect();

    liveCaptionSession.enabled = true;
    liveCaptionSession.connectionStatus = "connected";
    liveCaptionSession.sessionStartMs = Date.now();
    liveCaptionSession.seq = 1;
    liveCaptionSession.pendingEvent = null;
    projectState.incode = tcLib.tcClockToSec(getWallClockTimecode(), projectState.frameRate || 29.97);

    return true;
}

export async function disableLiveCaptionSession() {
    liveCaptionSession.enabled = false;
    liveCaptionSession.connectionStatus = "idle";
    liveCaptionSession.pendingEvent = null;

    if (activeAdapter) {
        await activeAdapter.disconnect?.();
        activeAdapter = null;
    }
}

export async function sendLiveCaptionLine(text = "") {
    const cleanText = text.trim();

    if (!liveCaptionSession.enabled) {
        throw new Error("Enable Live Captioning before sending captions.");
    }

    if (!cleanText) {
        return clearLiveCaptions();
    }

    const startSec = getElapsedSeconds();
    liveCaptionSession.connectionStatus = "sending";

    try {
        await ensureAdapter().send(cleanText, getSendContext());
        liveCaptionSession.connectionStatus = "connected";
        liveCaptionSession.lastError = "";
    } catch (err) {
        liveCaptionSession.connectionStatus = "error";
        liveCaptionSession.lastError = err.message;
        throw err;
    } finally {
        insertLiveCaptionEvent(cleanText, startSec);
        addTranscriptLine(cleanText);
    }

    return true;
}

export async function clearLiveCaptions() {
    if (!liveCaptionSession.enabled) {
        throw new Error("Enable Live Captioning before clearing captions.");
    }

    liveCaptionSession.connectionStatus = "sending";

    try {
        await ensureAdapter().clear(getSendContext());
        liveCaptionSession.connectionStatus = "connected";
        liveCaptionSession.lastError = "";
    } catch (err) {
        liveCaptionSession.connectionStatus = "error";
        liveCaptionSession.lastError = err.message;
        throw err;
    } finally {
        addTranscriptLine("", "clear");
    }

    return true;
}
